import { LoaderFunctionArgs } from "react-router-dom";
import { apiWobaInventory } from "@/services/api-woba-inventory";
import { AmenitiesData } from "@/components/amenities-form/amenities-form.types";
import { spaceService } from "@/services/space-service";
import { parseCookies } from "nookies";
import { COOKIES } from "@/constants/cookies";
import { apiLegacy } from "@/services/api-legacy";

interface PrivateOfficeData {
  externalSpaceId: string;
  spaceName: string;
  spaceRooms: any[];
  name: string;
  size: string;
  capacity: string;
  status: string;
  monthlyPrice: string;
  notes: string;
  description: string;
  photos: any[];
  videoUrls: string[];
  selectedAmenities: any[];
  operatingHours?: any;
  timezone?: string;
  neighborhood?: string | null;
  lifecycleState?: string;
  availabilityDate?: string;
}

interface LoaderData {
  privateOfficeData: PrivateOfficeData;
  amenitiesData: AmenitiesData | null;
  hasPermission: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  userInfo: {
    companyId: number;
    userId: number;
    email: string;
    isExternal: boolean;
  };
  error?: string;
  errorType?:
    | "ROOM_NOT_FOUND"
    | "ROOM_DELETED"
    | "PERMISSION_DENIED"
    | "UNKNOWN_ERROR";
  errorMessage?: string;
}

const fetchPrivateOfficeData = async (
  privateOfficeId: string,
): Promise<PrivateOfficeData> => {
  try {
    console.log(
      "API Woba Inventory - Fetching private office data for ID:",
      privateOfficeId,
    );

    const response = await apiWobaInventory.get(
      `/private-offices/${privateOfficeId}`,
    );
    const data = response.data;
    console.log(
      "API Woba Inventory - Raw API response:",
      JSON.stringify(data, null, 2),
    );

    const spaceId =
      data.parentId && Array.isArray(data.parentId) && data.parentId.length > 0
        ? data.parentId[0]
        : data.metadata?.partnerId || data.partnerId || data.spaceId || "";

    const metadata = data.metadata || {};

    if (data.deletedAt) {
      throw Object.assign(new Error("Sala deletada"), {
        isDeleted: true,
        deletedAt: data.deletedAt,
      });
    }

    return {
      externalSpaceId: spaceId,
      spaceName: data.spaceName || "",
      spaceRooms: data.spaceRooms || [],
      name: metadata.name || data.name || "",
      size:
        metadata.sizeM2?.toString() ||
        data.sizeM2?.toString() ||
        metadata.size?.toString() ||
        data.size?.toString() ||
        "",
      capacity:
        metadata.capacity?.toString() || data.capacity?.toString() || "",
      status: metadata.status || data.status || "available",
      monthlyPrice:
        metadata.monthlyPrice?.toString() ||
        data.monthlyPrice?.amount?.toString() ||
        data.monthlyPrice?.toString() ||
        "",
      notes: metadata.notes || data.notes || "",
      description: metadata.description || data.description || "",
      photos: metadata.photos || data.photos || [],
      videoUrls:
        metadata.video?.length > 0
          ? metadata.video
          : data.video?.length > 0
            ? data.video
            : metadata.videoUrl
              ? [metadata.videoUrl]
              : data.videoUrl
                ? [data.videoUrl]
                : [],
      selectedAmenities:
        metadata.amenities || data.selectedAmenities || data.amenities || [],
      operatingHours:
        metadata.operatingHours || data.operatingHours || undefined,
      timezone: metadata.timezone || data.timezone || "America/Sao_Paulo",
      lifecycleState:
        metadata.lifecycleState || data.lifecycleState || undefined,
      availabilityDate: metadata.availabilityDate
        ? new Date(metadata.availabilityDate).toISOString().split("T")[0]
        : data.availabilityDate
          ? new Date(data.availabilityDate).toISOString().split("T")[0]
          : undefined,
    };
  } catch (error) {
    console.error(
      "API Woba Inventory - Error fetching private office data:",
      error,
    );
    // Re-throw the flagged "deleted" error as-is so the loader's isDeleted
    // check stays reachable. Re-wrapping it in a plain Error (below) would
    // strip the isDeleted/deletedAt properties and route a deleted room to
    // the generic UNKNOWN_ERROR branch instead of ROOM_DELETED.
    if ((error as { isDeleted?: boolean })?.isDeleted) {
      throw error;
    }
    throw new Error(
      `Erro ao buscar dados da sala: ${error instanceof Error ? error.message : "Erro desconhecido"}`,
    );
  }
};

const fetchAmenitiesData = async (
  privateOfficeId?: string,
): Promise<AmenitiesData | null> => {
  try {
    const url = privateOfficeId
      ? `/amenities-form?privateOfficeId=${privateOfficeId}`
      : "/amenities-form";

    const response = await apiWobaInventory.get(url);
    const transformedData = {
      amenities: response.data,
    };
    return transformedData;
  } catch (error) {
    console.error(
      "API Woba Inventory - Error fetching amenities in loader:",
      error,
    );
    return null;
  }
};

const fetchSpaceNeighborhood = async (
  spaceId: string,
): Promise<string | null> => {
  try {
    if (!spaceId) {
      return null;
    }

    const space = await spaceService.getSpaceById({
      spaceId: Number(spaceId),
    });

    return space.neighborhood;
  } catch (error) {
    console.error("Error fetching space neighborhood in loader:", error);
    return null;
  }
};

export const editPrivateOfficeLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<LoaderData> => {
  const { privateOfficeId } = params;

  if (!privateOfficeId) {
    throw new Error("API Woba Inventory - ID da sala privativa não fornecido");
  }

  const cookies = parseCookies();
  const token = cookies[COOKIES.BACKOFFICE_AUTH] || cookies[COOKIES.U_TOKEN];
  const url = new URL(request.url);
  const isExternalRoute = url.pathname.includes("/external/");

  try {
    let userInfoData;
    try {
      if (token) {
        const userInfoResponse = await apiLegacy.get(
          `/v0/users/me?fl=profile,plan`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        userInfoData = userInfoResponse.data;
      }
    } catch (userError) {
      console.warn(
        "Warning: Failed to fetch user info, using fallback:",
        userError,
      );
    }
    const [privateOfficeData, amenitiesData] = await Promise.all([
      fetchPrivateOfficeData(privateOfficeId),
      fetchAmenitiesData(privateOfficeId),
    ]);

    const neighborhood = privateOfficeData.externalSpaceId
      ? await fetchSpaceNeighborhood(privateOfficeData.externalSpaceId)
      : null;

    privateOfficeData.neighborhood = neighborhood;

    if (isExternalRoute) {
      return {
        privateOfficeData,
        amenitiesData,
        hasPermission: true,
        canDelete: false,
        canUpdate: true,
        userInfo: {
          companyId: userInfoData?.company?.id || 0,
          userId: userInfoData?.user?.id || 0,
          email: userInfoData?.profile?.email || "unknown@user.com",
          isExternal: true,
        },
      };
    }

    return {
      privateOfficeData,
      amenitiesData,
      hasPermission: true,
      canDelete: true,
      canUpdate: true,
      userInfo: {
        companyId: userInfoData?.company?.id || 189,
        userId: userInfoData?.user?.id || 0,
        email: userInfoData?.profile?.email || "unknown@user.com",
        isExternal: false,
      },
    };
  } catch (error) {
    console.error(
      "API Woba Inventory - Erro ao carregar dados da sala:",
      error,
    );

    const isDeletedError =
      (error as { isDeleted?: boolean })?.isDeleted === true;

    if (isDeletedError) {
      return {
        privateOfficeData: {} as PrivateOfficeData,
        amenitiesData: null,
        hasPermission: true,
        canDelete: false,
        canUpdate: false,
        userInfo: {
          companyId: isExternalRoute ? 0 : 189,
          userId: 0,
          email: "error@user.com",
          isExternal: isExternalRoute,
        },
        error: "Sala privativa deletada",
        errorType: "ROOM_DELETED",
        errorMessage:
          "Esta sala privativa foi deletada e não pode mais ser editada.",
      };
    }

    const is404Error = (error as { response?: { statusCode?: number } })
      ?.toString()
      .includes("404");

    if (is404Error) {
      return {
        privateOfficeData: {} as PrivateOfficeData,
        amenitiesData: null,
        hasPermission: true,
        canDelete: false,
        canUpdate: false,
        userInfo: {
          companyId: isExternalRoute ? 0 : 189,
          userId: 0,
          email: "error@user.com",
          isExternal: isExternalRoute,
        },
        error: "Sala privativa não encontrada",
        errorType: "ROOM_NOT_FOUND",
        errorMessage:
          "Esta sala privativa não foi encontrada. Ela pode ter sido removida ou o ID está incorreto.",
      };
    }

    return {
      privateOfficeData: {} as PrivateOfficeData,
      amenitiesData: null,
      hasPermission: false,
      canDelete: false,
      canUpdate: false,
      userInfo: {
        companyId: 0,
        userId: 0,
        email: "error@user.com",
        isExternal: true,
      },
      error: error instanceof Error ? error.message : "Erro desconhecido",
      errorType: "UNKNOWN_ERROR",
      errorMessage: "Ocorreu um erro inesperado ao carregar os dados da sala.",
    };
  }
};
